import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const FaqPage = () => {
  const heroRef = useRef(null);
  const faqRef = useRef(null);
  const faqInView = useInView(faqRef, { once: true, amount: 0.1 });

  // Parallax effect for hero
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 500], [0, 120]);
  const heroImageScale = useTransform(scrollY, [0, 500], [1, 1.2]);

  const [searchTerm, setSearchTerm] = useState('');

  const faqs = [
    {
      category: 'Proceso',
      question: '¿Cuánto tiempo tarda una instalación de pladur en Terrassa?',
      answer: 'El tiempo de instalación depende del tipo y tamaño del proyecto. Un tabique sencillo de una habitación puede estar terminado en 1-2 días. Un trasdosado de una pared puede llevar 2-3 días. Falsos techos de una estancia requieren 2-4 días. Reformas completas de viviendas pueden necesitar entre 1 y 3 semanas. La gran ventaja del pladur es que es mucho más rápido que la obra tradicional y genera menos suciedad.',
      link: '/servicios',
      linkText: 'Ver todos nuestros servicios'
    },
    {
      category: 'Precio',
      question: '¿Qué factores afectan al precio de la instalación de pladur?',
      answer: 'Los principales factores que determinan el precio son: los metros cuadrados a instalar, la altura de los techos (a mayor altura, mayor dificultad), el tipo de placa de pladur (estándar, antihumedad, ignífuga o acústica), el nivel de aislamiento requerido (sin aislamiento, con lana de roca, doble placa), la complejidad del diseño (formas rectas o curvas, repisas, estanterías), el nivel de acabado (estándar o premium listo para pintar), y la accesibilidad de la zona de trabajo.',
      link: '/contacto',
      linkText: 'Solicitar presupuesto personalizado'
    },
    {
      category: 'Técnico',
      question: '¿El pladur aísla bien del ruido?',
      answer: 'Sí, el pladur puede proporcionar un excelente aislamiento acústico cuando se instala correctamente. Un tabique simple de pladur ya reduce el ruido, pero para un aislamiento acústico profesional utilizamos lana de roca de alta densidad entre las placas, sistema de doble placa, perfiles antivibratorios y sellado acústico de juntas. Con estas técnicas podemos reducir el ruido entre 40 y 55 decibelios, lo que supone una mejora muy significativa en el confort acústico.',
      link: '/servicios',
      linkText: 'Ver servicio de aislamiento acústico'
    },
    {
      category: 'Técnico',
      question: '¿El pladur proporciona aislamiento térmico?',
      answer: 'Sí, especialmente cuando se instala con material aislante. Los trasdosados de pladur con lana mineral pueden mejorar significativamente el aislamiento térmico de paredes y techos. Esto se traduce en menor consumo de calefacción en invierno y aire acondicionado en verano, mayor confort térmico en toda la vivienda y cumplimiento de normativas de eficiencia energética. El grosor del aislamiento se adapta según las necesidades de cada proyecto.',
      link: '/servicios',
      linkText: 'Ver servicio de aislamiento térmico'
    },
    {
      category: 'Técnico',
      question: '¿Se puede instalar pladur en baños y cocinas?',
      answer: 'Absolutamente. Para zonas húmedas como baños, cocinas y lavaderos utilizamos placas de pladur antihumedad (también llamadas hidrófugas), que son de color verde. Estas placas están específicamente diseñadas para resistir la humedad ambiental y son perfectas para alicatar encima. Son obligatorias en zonas de ducha, bañera y lavabos, y recomendables en toda la estancia del baño. Ofrecen la misma resistencia y durabilidad que el pladur estándar pero con protección adicional contra la humedad.',
      link: '/servicios',
      linkText: 'Ver servicio de pladur antihumedad'
    },
    {
      category: 'Proceso',
      question: '¿El acabado del pladur está listo para pintar?',
      answer: 'Sí, nuestros acabados de pladur quedan completamente lisos y listos para pintar directamente. Realizamos un tratamiento profesional de juntas con cinta y pasta, lijado fino de todas las superficies, imprimación de las placas si es necesario, y revisión final del acabado. Solo necesitarás aplicar la pintura que elijas. Si lo prefieres, también podemos aplicar papel pintado, estuco o cualquier otro revestimiento decorativo.',
      link: '/empresa',
      linkText: 'Conoce nuestra metodología de trabajo'
    },
    {
      category: 'Proceso',
      question: '¿Genera mucha suciedad la instalación de pladur?',
      answer: 'El pladur genera mucha menos suciedad que la obra tradicional. Es un sistema de construcción en seco, sin escombros de ladrillos ni cemento. La suciedad principal proviene del corte de placas (polvo de yeso) y del lijado de juntas. Nosotros tomamos medidas para minimizar la suciedad: protegemos suelos y muebles con plásticos, utilizamos aspiradores durante el corte y lijado, recogemos los residuos diariamente, y al finalizar dejamos el espacio limpio y recogido.',
      link: null,
      linkText: null
    },
    {
      category: 'Proceso',
      question: '¿Qué horarios de trabajo tenéis? ¿Habrá mucho ruido?',
      answer: 'Trabajamos en horario laboral estándar, normalmente de lunes a viernes de 8:00 a 18:00h, adaptándonos a tus necesidades si vives o trabajas en el espacio. El nivel de ruido es moderado y proviene principalmente del corte de placas y perfiles, y del atornillado. Es mucho menos ruidoso que la obra tradicional con taladros y martillos. Si trabajas desde casa o hay vecinos sensibles, podemos coordinar los trabajos más ruidosos en horarios específicos.',
      link: '/contacto',
      linkText: 'Contactar para coordinar horarios'
    },
    {
      category: 'Técnico',
      question: '¿Se pueden colgar cosas pesadas en paredes de pladur?',
      answer: 'Sí, se pueden colgar elementos pesados en pladur utilizando los anclajes adecuados. Para cuadros y elementos ligeros (hasta 5kg) se usan tacos de plástico estándar. Para elementos medios (5-15kg) como estanterías pequeñas, utilizamos tacos especiales para pladur tipo Molly o basculantes. Para elementos pesados (más de 15kg) como muebles, televisores o estanterías grandes, instalamos refuerzos de madera o metálicos dentro del tabique durante la instalación. Es importante planificar estos puntos antes de la instalación.',
      link: null,
      linkText: null
    },
    {
      category: 'Precio',
      question: '¿Emitís factura por los trabajos?',
      answer: 'Sí, emitimos factura por todos nuestros trabajos. Somos una empresa profesional y todos nuestros servicios incluyen factura con IVA, garantía por escrito del trabajo realizado, y cumplimiento de todas las normativas. La factura es importante para tener garantía legal del trabajo, poder deducir el IVA si eres autónomo o empresa, y justificar la inversión en tu vivienda si la vendes en el futuro.',
      link: '/empresa',
      linkText: 'Conoce más sobre nuestra empresa'
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categoryColors: { [key: string]: string } = {
    'Precio': 'bg-primary-yellow/20 text-primary-yellow',
    'Proceso': 'bg-accent-lime/20 text-accent-lime',
    'Técnico': 'bg-blue-400/20 text-blue-400'
  };

  return (
    <div className="bg-deep-green min-h-screen">
      <title>Preguntas Frecuentes sobre Pladur en Terrassa | FAQ | PLADUREX</title>
      <meta name="description" content="Resuelve tus dudas sobre instalación de pladur en Terrassa: precios, tiempos, aislamiento acústico y térmico, pladur antihumedad y más. Respuestas de expertos." />
      <meta name="keywords" content="preguntas pladur Terrassa, FAQ pladur, dudas instalación pladur, precio pladur Terrassa, tiempo instalación pladur, aislamiento pladur" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL}/faq`} />
      
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "name": "Preguntas Frecuentes sobre Pladur en Terrassa",
          "description": "Respuestas a las preguntas más frecuentes sobre instalación de pladur en Terrassa",
          "url": `${import.meta.env.VITE_SITE_URL}/faq`,
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": `${import.meta.env.VITE_SITE_URL}/`
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "FAQ",
                "item": `${import.meta.env.VITE_SITE_URL}/faq`
              }
            ]
          },
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Cuánto tiempo tarda una instalación de pladur en Terrassa?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "El tiempo de instalación depende del tipo y tamaño del proyecto. Un tabique sencillo de una habitación puede estar terminado en 1-2 días. Un trasdosado de una pared puede llevar 2-3 días. Falsos techos de una estancia requieren 2-4 días. Reformas completas de viviendas pueden necesitar entre 1 y 3 semanas. La gran ventaja del pladur es que es mucho más rápido que la obra tradicional y genera menos suciedad."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué factores afectan al precio de la instalación de pladur?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los principales factores que determinan el precio son: los metros cuadrados a instalar, la altura de los techos (a mayor altura, mayor dificultad), el tipo de placa de pladur (estándar, antihumedad, ignífuga o acústica), el nivel de aislamiento requerido (sin aislamiento, con lana de roca, doble placa), la complejidad del diseño (formas rectas o curvas, repisas, estanterías), el nivel de acabado (estándar o premium listo para pintar), y la accesibilidad de la zona de trabajo."
              }
            },
            {
              "@type": "Question",
              "name": "¿El pladur aísla bien del ruido?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, el pladur puede proporcionar un excelente aislamiento acústico cuando se instala correctamente. Un tabique simple de pladur ya reduce el ruido, pero para un aislamiento acústico profesional utilizamos lana de roca de alta densidad entre las placas, sistema de doble placa, perfiles antivibratorios y sellado acústico de juntas. Con estas técnicas podemos reducir el ruido entre 40 y 55 decibelios, lo que supone una mejora muy significativa en el confort acústico."
              }
            },
            {
              "@type": "Question",
              "name": "¿El pladur proporciona aislamiento térmico?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, especialmente cuando se instala con material aislante. Los trasdosados de pladur con lana mineral pueden mejorar significativamente el aislamiento térmico de paredes y techos. Esto se traduce en menor consumo de calefacción en invierno y aire acondicionado en verano, mayor confort térmico en toda la vivienda y cumplimiento de normativas de eficiencia energética. El grosor del aislamiento se adapta según las necesidades de cada proyecto."
              }
            },
            {
              "@type": "Question",
              "name": "¿Se puede instalar pladur en baños y cocinas?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutamente. Para zonas húmedas como baños, cocinas y lavaderos utilizamos placas de pladur antihumedad (también llamadas hidrófugas), que son de color verde. Estas placas están específicamente diseñadas para resistir la humedad ambiental y son perfectas para alicatar encima. Son obligatorias en zonas de ducha, bañera y lavabos, y recomendables en toda la estancia del baño. Ofrecen la misma resistencia y durabilidad que el pladur estándar pero con protección adicional contra la humedad."
              }
            },
            {
              "@type": "Question",
              "name": "¿El acabado del pladur está listo para pintar?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, nuestros acabados de pladur quedan completamente lisos y listos para pintar directamente. Realizamos un tratamiento profesional de juntas con cinta y pasta, lijado fino de todas las superficies, imprimación de las placas si es necesario, y revisión final del acabado. Solo necesitarás aplicar la pintura que elijas. Si lo prefieres, también podemos aplicar papel pintado, estuco o cualquier otro revestimiento decorativo."
              }
            },
            {
              "@type": "Question",
              "name": "¿Genera mucha suciedad la instalación de pladur?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "El pladur genera mucha menos suciedad que la obra tradicional. Es un sistema de construcción en seco, sin escombros de ladrillos ni cemento. La suciedad principal proviene del corte de placas (polvo de yeso) y del lijado de juntas. Nosotros tomamos medidas para minimizar la suciedad: protegemos suelos y muebles con plásticos, utilizamos aspiradores durante el corte y lijado, recogemos los residuos diariamente, y al finalizar dejamos el espacio limpio y recogido."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué horarios de trabajo tenéis? ¿Habrá mucho ruido?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Trabajamos en horario laboral estándar, normalmente de lunes a viernes de 8:00 a 18:00h, adaptándonos a tus necesidades si vives o trabajas en el espacio. El nivel de ruido es moderado y proviene principalmente del corte de placas y perfiles, y del atornillado. Es mucho menos ruidoso que la obra tradicional con taladros y martillos. Si trabajas desde casa o hay vecinos sensibles, podemos coordinar los trabajos más ruidosos en horarios específicos."
              }
            },
            {
              "@type": "Question",
              "name": "¿Se pueden colgar cosas pesadas en paredes de pladur?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, se pueden colgar elementos pesados en pladur utilizando los anclajes adecuados. Para cuadros y elementos ligeros (hasta 5kg) se usan tacos de plástico estándar. Para elementos medios (5-15kg) como estanterías pequeñas, utilizamos tacos especiales para pladur tipo Molly o basculantes. Para elementos pesados (más de 15kg) como muebles, televisores o estanterías grandes, instalamos refuerzos de madera o metálicos dentro del tabique durante la instalación. Es importante planificar estos puntos antes de la instalación."
              }
            },
            {
              "@type": "Question",
              "name": "¿Emitís factura por los trabajos?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, emitimos factura por todos nuestros trabajos. Somos una empresa profesional y todos nuestros servicios incluyen factura con IVA, garantía por escrito del trabajo realizado, y cumplimiento de todas las normativas. La factura es importante para tener garantía legal del trabajo, poder deducir el IVA si eres autónomo o empresa, y justificar la inversión en tu vivienda si la vendes en el futuro."
              }
            }
          ]
        })}
      </script>

      <Header />
      <WhatsAppButton />
      <ScrollToTopButton />

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[40vh] min-h-[400px] flex items-center justify-center overflow-hidden pt-20">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroImageY }}
        >
          <motion.img
            src="https://public.readdy.ai/ai/img_res/a260719256cea8c01f28a0aec638d0ea.jpg"
            alt="Preguntas Frecuentes"
            className="w-full h-[120%] object-cover object-top"
            style={{ scale: heroImageScale }}
          />
          <div className="absolute inset-0 bg-deep-green/70"></div>
        </motion.div>

        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-primary-yellow transition-colors">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary-yellow">FAQ</span>
            </nav>
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-text-light mb-4">
              Preguntas Frecuentes sobre Pladur
            </h1>
            <p className="text-xl text-gray-300">
              Resolvemos tus dudas sobre instalación de pladur en Terrassa
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="py-20">
        <div className="max-w-[900px] mx-auto px-6">
          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar pregunta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 pl-14 bg-card-green border-2 border-card-green focus:border-primary-yellow rounded-lg text-text-light placeholder-gray-400 outline-none transition-colors text-sm"
              />
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-primary-yellow text-xl"></i>
            </div>
          </motion.div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => (
                <FAQItem key={index} faq={faq} index={index} inView={faqInView} categoryColors={categoryColors} />
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <i className="ri-search-line text-6xl text-gray-600 mb-4"></i>
                <p className="text-gray-400 text-lg">No se encontraron preguntas que coincidan con tu búsqueda</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-card-green to-black">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
              ¿No encuentras tu respuesta?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contacta con nosotros y te ayudaremos personalmente
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+34629501941"
                className="px-10 py-5 bg-primary-yellow text-deep-green font-bold text-lg rounded-lg hover:shadow-[0_0_40px_rgba(255,212,0,0.6)] hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Llamar Ahora
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-5 border-2 border-primary-yellow text-primary-yellow font-bold text-lg rounded-lg hover:bg-primary-yellow hover:text-deep-green transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FAQItem = ({ faq, index, inView, categoryColors }: { faq: any; index: number; inView: boolean; categoryColors: any }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="bg-card-green rounded-lg overflow-hidden hover:border-l-4 hover:border-primary-yellow transition-all duration-300"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-start justify-between text-left cursor-pointer"
      >
        <div className="flex-1 pr-4">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2 ${categoryColors[faq.category]}`}>
            {faq.category}
          </span>
          <h4 className="text-lg font-bold text-text-light">{faq.question}</h4>
        </div>
        <i className={`ri-add-line text-primary-yellow text-2xl transition-transform duration-300 flex-shrink-0 w-8 h-8 flex items-center justify-center ${isOpen ? 'rotate-45' : ''}`}></i>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-6 pb-5 pt-0">
          <p className="text-gray-300 leading-relaxed mb-3">{faq.answer}</p>
          {faq.link && (
            <Link
              to={faq.link}
              className="inline-flex items-center gap-2 text-primary-yellow hover:text-accent-lime transition-colors text-sm font-semibold"
            >
              {faq.linkText}
              <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FaqPage;
